import React, {useState} from 'react';

import * as biojs from 'BioJS';
import * as CrisprConstants from 'constants/CRISPRConstants';
import {CRISPRPam, PAM, PAM_INFO_BY_PAM} from 'constants/CRISPRConstants';
import FormInput from 'ui-library/bs/FormInput';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {InputChangeEvent} from 'util/Types';

const CUSTOM_PAM_TEXT = 'Custom PAM';
const PAMS = CrisprConstants.ORDERED_PAMS.map((pam) => CrisprConstants.PAM_INFO_BY_PAM[pam]);

export interface PAMSelectorProps {
  pamLink: {
    value: string;
    isLeftPam?: boolean;
    requestChange: (value: string, isLeftPam?: boolean) => void;
  };
  disabled?: boolean;
}

export function makeOptionText(pamInfo: CRISPRPam): string {
  const variant = pamInfo.nucleaseVariant ? ` ${pamInfo.nucleaseVariant}` : '';
  return `${pamInfo.bases} (${pamInfo.nucleaseNames.join('/')}${variant}, ${
    pamInfo.isLeftPam ? "5'" : "3'"
  } side)`;
}

export default function PAMSelector({disabled = false, pamLink}: PAMSelectorProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-in-operator
  const [usingCustomPam, setUsingCustomPam] = useState(!(pamLink.value in PAM));

  function presetPamChangeHandler(selectedValue: string): void {
    // eslint-disable-next-line benchlint/no-in-operator
    if (selectedValue in PAM) {
      setUsingCustomPam(false);
      pamLink.requestChange(selectedValue.toUpperCase(), pamLink.isLeftPam);
    } else {
      setUsingCustomPam(true);
      pamLink.requestChange('', pamLink.isLeftPam);
    }
  }

  function customPamChangeHandler(e: InputChangeEvent): void {
    const value = e.target.value.toUpperCase();
    if (!biojs.util.isValidSequence(value, biojs.util.NucleotideType.DNA)) {
      return;
    }
    pamLink.requestChange(value, pamLink.isLeftPam);
  }

  function sideChangeHandler(isLeftPam: boolean): void {
    pamLink.requestChange(pamLink.value, isLeftPam);
  }

  const pamOptions = [
    {
      name: CUSTOM_PAM_TEXT,
      value: CUSTOM_PAM_TEXT,
      dataTestAttrs: {component: 'PAMSelector', element: 'option', key: 'custom'},
    },
    ...PAMS.map((pam) => ({
      name: makeOptionText(pam),
      value: pam.bases,
      dataTestAttrs: {component: 'PAMSelector', element: 'option', key: pam.bases},
    })),
  ];

  const pamLengthMessage =
    pamLink.value.length < CrisprConstants.MINIMUM_PAM_LENGTH
      ? 'Custom PAM must be between 2 and 8 bases long.'
      : '';

  return (
    <div>
      <SelectDropdown
        dataTestAttrs={{component: 'PAMSelector', element: 'selectRoot'}}
        disabled={disabled}
        displayName={usingCustomPam ? CUSTOM_PAM_TEXT : makeOptionText(PAM_INFO_BY_PAM[pamLink.value as PAM])}
        menu={
          <SearchableMenu
            onSelect={(selectedValue) => {
              presetPamChangeHandler(selectedValue);
            }}
            optionDatas={pamOptions}
            selectedValue={usingCustomPam ? CUSTOM_PAM_TEXT : pamLink.value}
          />
        }
      />
      {usingCustomPam && (
        <div>
          <div>
            <FormInput
              className="u-mini-spacing-top u-mini-spacing-bottom"
              autoFocus={true}
              placeholder="Enter PAM"
              value={pamLink.value === CUSTOM_PAM_TEXT ? '' : pamLink.value}
              maxLength={CrisprConstants.MAXIMUM_PAM_LENGTH}
              onChange={customPamChangeHandler}
            />
          </div>
          <p className="help-block">{pamLengthMessage}</p>
          <RadioButtonSelector
            disabled={!usingCustomPam}
            selectedValue={pamLink.isLeftPam}
            options={[
              {label: "3'", value: false},
              {label: "5'", value: true},
            ]}
            onChange={sideChangeHandler}
          />
        </div>
      )}
    </div>
  );
}
